import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  getFirestore,
  doc,
  collection,
  getDoc,
  updateDoc,
  getDocs,
  query,
  where,
  Timestamp,
  setDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../firebase/AuthContext";
import {
  auditStamp,
  completeDespatchAndNotify,
  completeDespatchRecord,
  createDespatchRecord,
} from "./despatchReportHelper";

const DespatchReport = () => {
  const firestore = getFirestore();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // ---------------- State ----------------
  const [reportDocId, setReportDocId] = useState(null);
  const [reportNumber, setReportNumber] = useState(null);
  const [barcodes, setBarcodes] = useState([]);
  const [currentBarcode, setCurrentBarcode] = useState("");
  const [bgColorClass, setBgColorClass] = useState("");
  const [productCodeCache, setProductCodeCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");     // ✅ NEW
  const [loadingDetail, setLoadingDetail] = useState(""); // ✅ NEW

  const [operationError, setOperationError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [lastSyncMsg, setLastSyncMsg] = useState("");


  const barcodeInputRef = useRef(null);

  // Prevent overlapping flushes (interval + manual + quick flush)
  const flushLockRef = useRef(false);
  const cancelledRef = useRef(false);

  // ---------------- Local storage keys ----------------
  const LOCAL_ITEMS_KEY = (docId) => `despatch_local_items_${docId}`;
  const OUTBOX_KEY = (docId) => `despatch_outbox_${docId}`;

  // Write-ahead-log backup (append-only journal)
  const WAL_KEY = (docId) => `despatch_wal_${docId}`;
  const WAL_MAX = 2000; // keep last N events

  // ---------------- Utilities ----------------
  const flashColor = (color) => {
    setBgColorClass(color);
    setTimeout(() => setBgColorClass(""), 800);
  };

  const focusBarcodeInput = (force = false) => {
    setTimeout(() => {
      const el = barcodeInputRef.current;
      if (!el) return;

      const y = window.scrollY || 0;
      const isReading = y > 120;
      if (!force && isReading) return;

      el.focus({ preventScroll: true });
      el.select?.();
    }, 0);
  };

  const withTimeout = (promise, ms, label = "timeout") => {
    let t;
    const timeout = new Promise((_, reject) => {
      t = setTimeout(() => reject(new Error(label)), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(t));
  };

  const normalizeItem = (it) => {
    const sync = it?._sync || "synced";
    return { ...it, _sync: sync };
  };

  const dedupeByIdPreferFirst = (items) => {
    const map = new Map();
    for (const it of items) {
      if (!it?.id) continue;
      if (!map.has(it.id)) map.set(it.id, it); // prefer first
    }
    return Array.from(map.values());
  };

  const nowMs = () => Date.now();

  // ---------------- Storage layer (redundant, WAL) ----------------
  const safeReadJson = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw);
    } catch {}
    // fallback to sessionStorage if localStorage is blocked/quota/corrupt
    try {
      const raw2 = sessionStorage.getItem(key);
      if (raw2) return JSON.parse(raw2);
    } catch {}
    return null;
  };

  const safeWriteJson = (key, val) => {
    const str = JSON.stringify(val);
    try {
      localStorage.setItem(key, str);
    } catch {
      // ignore
    }
    try {
      sessionStorage.setItem(key, str);
    } catch {
      // ignore
    }
  };

  const loadLocalItems = (docId) => {
    const arr = safeReadJson(LOCAL_ITEMS_KEY(docId));
    if (!Array.isArray(arr)) return [];
    return arr.map(normalizeItem);
  };

  const saveLocalItems = (docId, items) => {
    safeWriteJson(LOCAL_ITEMS_KEY(docId), items);
  };

  const loadOutbox = (docId) => {
    const arr = safeReadJson(OUTBOX_KEY(docId));
    return Array.isArray(arr) ? arr : [];
  };

  const saveOutbox = (docId, items) => {
    safeWriteJson(OUTBOX_KEY(docId), items);
  };

  const loadWal = (docId) => {
    const arr = safeReadJson(WAL_KEY(docId));
    return Array.isArray(arr) ? arr : [];
  };

  const appendWal = (docId, entry) => {
    try {
      const wal = loadWal(docId);
      wal.push(entry);
      const trimmed = wal.length > WAL_MAX ? wal.slice(wal.length - WAL_MAX) : wal;
      safeWriteJson(WAL_KEY(docId), trimmed);
    } catch {
      // ignore
    }
  };

  const rebuildFromWal = (docId, baseItems) => {
    // Replay WAL on top of baseItems to reconstruct if anything went missing locally.
    // WAL entries are authoritative for "scan" and "delete" actions.
    const wal = loadWal(docId);
    if (!wal.length) return baseItems;

    const byId = new Map(baseItems.map((x) => [x.id, x]));

    for (const e of wal) {
      if (!e || !e.type) continue;
      if (e.type === "scan" && e.item?.id) {
        // keep most recent version of the item (WAL is chronological)
        byId.set(e.item.id, e.item);
      }
      if (e.type === "delete" && e.itemId) {
        byId.delete(e.itemId);
      }
      if (e.type === "patch" && e.itemId) {
        const cur = byId.get(e.itemId);
        if (cur) byId.set(e.itemId, { ...cur, ...e.patch });
      }
    }

    // Return newest first (your UI expects newest at top)
    return Array.from(byId.values()).sort((a, b) => {
      const at = a?.scannedAt?.toMillis?.() ?? 0;
      const bt = b?.scannedAt?.toMillis?.() ?? 0;
      return bt - at;
    });
  };

  // ---------------- Outbox helpers ----------------
  const upsertOutboxItem = (docId, entry) => {
    const outbox = loadOutbox(docId);
    const idx = outbox.findIndex((x) => x.id === entry.id);
    const next =
      idx >= 0
        ? [...outbox.slice(0, idx), entry, ...outbox.slice(idx + 1)]
        : [...outbox, entry];
    saveOutbox(docId, next);
    return next;
  };

  const removeOutboxItem = (docId, entryId) => {
    const next = loadOutbox(docId).filter((x) => x.id !== entryId);
    saveOutbox(docId, next);
    return next;
  };

  const queueForUpload = (docId, item) => {
    upsertOutboxItem(docId, {
      id: item.id,
      item,
      attempts: 0,
      lastAttemptAt: 0,
    });
  };

  // ---------------- Firestore: production-safe UPSERT/DELETE by id ----------------
  const upsertFirestoreItemById = async (docId, item) => {
    const detailedRef = doc(firestore, "despatchreportsdetailed", docId);

    // Strip local-only fields
    const { _sync, ...fireItem } = item;

    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(detailedRef);
      const existing = snap.exists() ? snap.data().items || [] : [];

      const idx = existing.findIndex((x) => x?.id === fireItem.id);

      let next;
      if (idx >= 0) {
        // Merge upgrade (don’t regress productCode to Unknown)
        const cur = existing[idx] || {};
        const merged = { ...cur, ...fireItem };

        // If we already have a good productCode, don’t overwrite it with Unknown(...)
        if (
          typeof cur.productCode === "string" &&
          cur.productCode &&
          !/^Unknown/.test(cur.productCode) &&
          typeof fireItem.productCode === "string" &&
          /^Unknown/.test(fireItem.productCode)
        ) {
          merged.productCode = cur.productCode;
        }

        next = [...existing];
        next[idx] = merged;
      } else {
        next = [...existing, fireItem];
      }

      // Ensure doc exists
      if (!snap.exists()) tx.set(detailedRef, { items: next }, { merge: true });
      else tx.update(detailedRef, { items: next });
    });
  };

  const deleteFirestoreItemById = async (docId, itemId) => {
    const detailedRef = doc(firestore, "despatchreportsdetailed", docId);
    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(detailedRef);
      if (!snap.exists()) return;
      const existing = snap.data().items || [];
      const next = existing.filter((x) => x?.id !== itemId);
      tx.update(detailedRef, { items: next });
    });
  };

  const patchFirestoreItemById = async (docId, itemId, patch) => {
    // Merge-only patch (safe hydration). Never overwrites the array with a subset.
    const detailedRef = doc(firestore, "despatchreportsdetailed", docId);
    await runTransaction(firestore, async (tx) => {
      const snap = await tx.get(detailedRef);
      if (!snap.exists()) return;
      const existing = snap.data().items || [];
      const idx = existing.findIndex((x) => x?.id === itemId);
      if (idx < 0) return;

      const cur = existing[idx] || {};
      const merged = { ...cur, ...patch };

      // same protection as above
      if (
        typeof cur.productCode === "string" &&
        cur.productCode &&
        !/^Unknown/.test(cur.productCode) &&
        typeof patch.productCode === "string" &&
        /^Unknown/.test(patch.productCode)
      ) {
        merged.productCode = cur.productCode;
      }

      const next = [...existing];
      next[idx] = merged;
      tx.update(detailedRef, { items: next });
    });
  };

  // ---------------- 1) Find incomplete / create new ----------------
  const findIncompleteDespatch = async () => {
    const despatchReportsRef = collection(firestore, "despatchreports");
    const q = query(despatchReportsRef, where("completed", "==", false));
    const snap = await getDocs(q);
    if (snap.empty) return null;

    const incompleteDocs = [];
    snap.forEach((docSnap) => {
      incompleteDocs.push({ docId: docSnap.id, ...docSnap.data() });
    });
    incompleteDocs.sort((a, b) => (b.reportNumber ?? 0) - (a.reportNumber ?? 0));
    const newest = incompleteDocs[0];

    const detailRef = doc(firestore, "despatchreportsdetailed", newest.docId);
    let detailSnap = await getDoc(detailRef);
    if (!detailSnap.exists()) {
      await setDoc(detailRef, { items: [] }, { merge: true });
      detailSnap = await getDoc(detailRef);
    }
    const items = detailSnap.data().items || [];

    return {
      docId: newest.docId,
      reportNumber: newest.reportNumber,
      items,
    };
  };

  const createNewDespatch = async () => {
    const countersRef = doc(firestore, "appData", "counters");
    const summaryRef = doc(collection(firestore, "despatchreports"));
    const detailedRef = doc(
      firestore,
      "despatchreportsdetailed",
      summaryRef.id
    );

    return createDespatchRecord({
      firestore,
      countersRef,
      summaryRef,
      detailedRef,
      user: currentUser,
      runTransactionFn: runTransaction,
      serverTimestampFn: serverTimestamp,
    });
  };

  // ---------------- 2) Product code fetch (never blocks scanning) ----------------
  const fetchProductCode = async (productId) => {
    if (productCodeCache[productId]) return productCodeCache[productId];

    try {
      const cylRef = doc(firestore, "cyl_info", productId);
      const cylSnap = await getDoc(cylRef);
      const productCode = cylSnap.exists()
        ? cylSnap.data().productCode
        : `Unknown (${productId})`;

      setProductCodeCache((prev) => ({ ...prev, [productId]: productCode }));
      return productCode;
    } catch (error) {
      console.error("Error fetching product code:", error);
      return `Unknown (${productId})`;
    }
  };

  const hydrateOneItemIfNeeded = async (docId, item) => {
    if (!item?.id || !item?.productId) return;
    if (item.productCode && !/^Unknown/.test(item.productCode)) return;

    const code = await fetchProductCode(item.productId);
    if (!code || /^Unknown/.test(code)) return;

    // Patch local + WAL
    appendWal(docId, { t: nowMs(), type: "patch", itemId: item.id, patch: { productCode: code } });

    // Update UI + local storage fast
    setBarcodes((prev) => {
      const next = prev.map((x) => (x.id === item.id ? { ...x, productCode: code } : x));
      saveLocalItems(docId, next);
      return next;
    });

    // If it is already synced in firestore, patch it safely (merge-only)
    if (item._sync === "synced") {
      try {
        await withTimeout(patchFirestoreItemById(docId, item.id, { productCode: code }), 1200, "hydrate_fs_slow");
      } catch {
        // best effort, leave it — completion already checks unknowns
      }
    }
  };

  // ---------------- 3) Factory barcode instance update (best-effort) ----------------
  const updateFactoryBarcodeInstance = async (barcode) => {
    const barcodeRef = doc(firestore, "factorybarcode", barcode);
    const instancesSnap = await getDocs(collection(barcodeRef, "instances"));

    const unscanned = instancesSnap.docs
      .filter((d) => !d.data().despatch_scan)
      .sort((a, b) => {
        const aDate = a.data().PrintedAt?.toDate?.() || new Date(0);
        const bDate = b.data().PrintedAt?.toDate?.() || new Date(0);
        return aDate - bDate;
      });

    if (unscanned.length === 0) return;

    const oldest = unscanned[0];
    await updateDoc(doc(barcodeRef, "instances", oldest.id), {
      despatch_scan: true,
      despatch_scantime: Timestamp.now(),
    });
  };

  // ---------------- 4) Upload one scan (idempotent, timeboxed) ----------------
  const uploadOneScan = async (docId, item) => {
    // Firestore must be idempotent and never create duplicates
    await withTimeout(upsertFirestoreItemById(docId, item), 1400, "firestore_slow");

    // Factory update is best-effort and must not block
    try {
      await withTimeout(updateFactoryBarcodeInstance(item.barcode), 1200, "factory_slow");
    } catch (e) {
      console.warn("Factory update deferred:", e?.message || e);
    }
  };

  // ---------------- 5) Outbox flusher (locked, backoff) ----------------
  const flushOutbox = useCallback(
    async (docId, opts = {}) => {
      const limit = opts.limit ?? 10;
      const force = opts.force ?? false;

      if (!docId) return { attempted: 0, succeeded: 0, remaining: 0 };
      if (!navigator.onLine) return { attempted: 0, succeeded: 0, remaining: loadOutbox(docId).length };

      // lock to prevent overlapping runs
      if (flushLockRef.current) return { attempted: 0, succeeded: 0, remaining: loadOutbox(docId).length };
      flushLockRef.current = true;

      try {
        const outbox = loadOutbox(docId);
        if (!outbox.length) return { attempted: 0, succeeded: 0, remaining: 0 };

        let attempted = 0;
        let succeeded = 0;

        for (const entry of outbox.slice(0, limit)) {
          if (cancelledRef.current) break;

          attempted++;

          const attempts = entry.attempts || 0;
          const last = entry.lastAttemptAt || 0;
          const backoffMs = Math.min(30000, 700 * Math.pow(2, attempts));

          if (!force && nowMs() - last < backoffMs) continue;

          upsertOutboxItem(docId, { ...entry, attempts: attempts + 1, lastAttemptAt: nowMs() });

          try {
            await uploadOneScan(docId, entry.item);

            // remove from outbox
            removeOutboxItem(docId, entry.id);

            // mark synced locally + WAL
            appendWal(docId, { t: nowMs(), type: "patch", itemId: entry.id, patch: { _sync: "synced" } });

            setBarcodes((prev) => {
              const next = prev.map((x) => (x.id === entry.id ? { ...x, _sync: "synced" } : x));
              saveLocalItems(docId, next);
              return next;
            });

            succeeded++;
          } catch {
            // keep pending
          }
        }

        return { attempted, succeeded, remaining: loadOutbox(docId).length };
      } finally {
        flushLockRef.current = false;
      }
    },
    [firestore]
  );

  // ---------------- 6) Reconciliation (Firestore vs Local vs WAL vs Outbox) ----------------
  const reconcileAll = async (docId) => {
    if (!docId) return { mergedFromFirestore: 0, queuedForUpload: 0, outboxRemaining: 0 };

    const detailedRef = doc(firestore, "despatchreportsdetailed", docId);
    const snap = await getDoc(detailedRef);
    const firestoreItemsRaw = snap.exists() ? snap.data().items || [] : [];
    const firestoreItems = firestoreItemsRaw.map((x) => ({ ...x, _sync: "synced" }));

    const localItems0 = loadLocalItems(docId);

    // Rebuild local from WAL to recover anything that "fell out"
    const localItems = rebuildFromWal(docId, localItems0);

    const outbox = loadOutbox(docId);
    const outboxIds = new Set(outbox.map((x) => x.id));

    const fsById = new Map(firestoreItems.map((x) => [x.id, x]));
    const localById = new Map(localItems.map((x) => [x.id, x]));

    let mergedFromFirestore = 0;
    let queuedForUpload = 0;

    // A) Firestore -> Local
    for (const [id, fsItem] of fsById.entries()) {
      if (!localById.has(id)) {
        localById.set(id, fsItem);
        mergedFromFirestore++;
      } else {
        const l = localById.get(id);
        if (l?._sync !== "synced") localById.set(id, { ...l, _sync: "synced" });
      }
    }

    // B) Local -> Outbox
    for (const [id, localItem] of localById.entries()) {
      if (!fsById.has(id)) {
        if (!outboxIds.has(id)) {
          queueForUpload(docId, { ...localItem, _sync: "pending" });
          queuedForUpload++;
        }
        localById.set(id, { ...localItem, _sync: "pending" });
      }
    }

    const merged = dedupeByIdPreferFirst(
      // prefer newest: sort by scannedAt desc, then dedupe prefer-first
      Array.from(localById.values()).sort((a, b) => {
        const at = a?.scannedAt?.toMillis?.() ?? 0;
        const bt = b?.scannedAt?.toMillis?.() ?? 0;
        return bt - at;
      })
    );

    saveLocalItems(docId, merged);
    setBarcodes(merged);

    return {
      mergedFromFirestore,
      queuedForUpload,
      outboxRemaining: loadOutbox(docId).length,
    };
  };

  // ---------------- Mount: find/create + merge ----------------
  useEffect(() => {
    cancelledRef.current = false;
    focusBarcodeInput();

    (async () => {
      try {
        const existing = await findIncompleteDespatch();
        if (existing) {
          setReportDocId(existing.docId);
          setReportNumber(existing.reportNumber);

          const local0 = loadLocalItems(existing.docId);
          const firestoreItems = (existing.items ?? []).map((x) => ({ ...x, _sync: "synced" }));

          // Merge, prefer local first so newest local isn't lost
          const merged0 = dedupeByIdPreferFirst([...local0, ...firestoreItems]);
          saveLocalItems(existing.docId, merged0);

          // Rebuild from WAL on top (paranoid recovery)
          const merged = rebuildFromWal(existing.docId, merged0);

          saveLocalItems(existing.docId, merged);
          setBarcodes(merged);

          await reconcileAll(existing.docId);

          // Kick hydration for any Unknowns in the background
          const snapshot = loadLocalItems(existing.docId);
          snapshot.forEach((it) => {
            hydrateOneItemIfNeeded(existing.docId, it).catch(() => {});
          });
        } else {
          const { docId, number } = await createNewDespatch();
          setReportDocId(docId);
          setReportNumber(number);

          saveLocalItems(docId, []);
          saveOutbox(docId, []);
          safeWriteJson(WAL_KEY(docId), []);
        }
      } catch (err) {
        console.error("Error checking/creating despatch:", err);
        setOperationError(
          err?.message ||
            "Could not open a despatch report. Please try again."
        );
      } finally {
        focusBarcodeInput();
      }
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // ---------------- Background flush loop ----------------
  useEffect(() => {
    if (!reportDocId) return;

    const tick = async () => {
      if (cancelledRef.current) return;

      const remaining = loadOutbox(reportDocId).length;
      if (!remaining) return;

      setSyncing(true);
      const res = await flushOutbox(reportDocId, { limit: 8, force: false });
      setLastSyncMsg(
        res.succeeded > 0
          ? `Synced ${res.succeeded}. Pending ${res.remaining}.`
          : `Pending ${res.remaining}.`
      );
      setSyncing(false);
    };

    const interval = setInterval(tick, 1500);
    window.addEventListener("online", tick);

    tick();

    return () => {
      clearInterval(interval);
      window.removeEventListener("online", tick);
    };
  }, [reportDocId, flushOutbox]);

  // ---------------- Scanning ----------------
  const handleBarcodeChange = (e) => {
    const numericOnly = e.target.value.replace(/\D/g, "");
    setCurrentBarcode(numericOnly);
  };

  const isBarcodeValid = useMemo(() => currentBarcode.length === 13, [currentBarcode]);

  const handleBarcodeKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();

    if (!isBarcodeValid || !reportDocId) {
      flashColor("bg-red-500");
      setCurrentBarcode("");
      focusBarcodeInput();
      return;
    }

    // Capture instantly. Do not await anything that could block rapid scanning.
    const barcode = currentBarcode;
    setCurrentBarcode("");

    const serialNumber = barcode.slice(0, 6);
    const special = barcode.slice(6, 8);
    const productId = barcode.slice(8);
    const specialLabel = special === "49" ? "ORDER" : special === "50" ? "STOCK" : special;

    // Default unknown; hydrate later asynchronously
    const initialProductCode = productCodeCache[productId] || `Unknown (${productId})`;

    const newItem = normalizeItem({
      id: uuidv4(),
      barcode,
      serialNumber,
      productId,
      productCode: initialProductCode,
      special: specialLabel,
      scannedAt: Timestamp.now(),
      _sync: "pending",
    });

    // WAL first (write-ahead-log): if browser crashes mid-write, we can rebuild
    appendWal(reportDocId, { t: nowMs(), type: "scan", item: newItem });

    // Update UI + local storage immediately
    setBarcodes((prev) => {
      const next = dedupeByIdPreferFirst([newItem, ...prev]);
      saveLocalItems(reportDocId, next);
      return next;
    });

    // Queue for upload immediately
    queueForUpload(reportDocId, newItem);

    flashColor("bg-green-500");
    focusBarcodeInput();

    // Hydrate product code in background (doesn't block scanning)
    hydrateOneItemIfNeeded(reportDocId, newItem).catch(() => {});

    // Quick flush attempt (non-blocking, locked)
    flushOutbox(reportDocId, { limit: 3, force: true }).then((res) => {
      if (res.succeeded > 0) flashColor("bg-green-500");
    });
  };

  // ---------------- Delete item ----------------
  const handleDeleteItem = async (itemToDelete) => {
    if (!reportDocId) return;

    // WAL delete
    appendWal(reportDocId, { t: nowMs(), type: "delete", itemId: itemToDelete.id });

    // Local remove first (fast UI)
    setBarcodes((prev) => {
      const next = prev.filter((x) => x.id !== itemToDelete.id);
      saveLocalItems(reportDocId, next);
      return next;
    });

    // Remove from outbox so it won’t re-upload
    removeOutboxItem(reportDocId, itemToDelete.id);

    flashColor("bg-yellow-500");
    focusBarcodeInput();

    // If synced: delete from Firestore safely (transaction remove-by-id, not overwrite array)
    if (itemToDelete._sync === "synced") {
      try {
        await withTimeout(deleteFirestoreItemById(reportDocId, itemToDelete.id), 2000, "delete_fs_slow");
      } catch (error) {
        console.error("Error deleting synced item from Firestore:", error);
      }
    }
  };

  // ---------------- Manual retry helpers ----------------
  const pendingCount = useMemo(() => {
    if (!reportDocId) return 0;
    return loadOutbox(reportDocId).length;
  }, [reportDocId, barcodes, syncing]);

  const handleRetryPending = async () => {
    if (!reportDocId) return;
    setSyncing(true);
    setLastSyncMsg("Retrying pending uploads...");
    const res = await flushOutbox(reportDocId, { limit: 200, force: true });
    setLastSyncMsg(`Synced ${res.succeeded}. Pending ${res.remaining}.`);
    setSyncing(false);
    focusBarcodeInput();
  };

  const handleRetryOne = async (item) => {
    if (!reportDocId) return;
    queueForUpload(reportDocId, { ...item, _sync: "pending" });
    setSyncing(true);
    const res = await flushOutbox(reportDocId, { limit: 1, force: true });
    setLastSyncMsg(res.succeeded ? "Item synced." : "Still pending.");
    setSyncing(false);
    focusBarcodeInput();
  };

  // ---------------- Complete ----------------
  const handleCompleteDespatch = async () => {
    if (!reportDocId) return;

    if (!auditStamp(currentUser)) {
      const message =
        "You must be signed in before completing a despatch report. Please sign in and try again.";
      setOperationError(message);
      alert(message);
      focusBarcodeInput();
      return;
    }

    setOperationError("");

    if (barcodes.length === 0) {
      alert("You can't complete a despatch with no scanned items.");
      focusBarcodeInput();
      return;
    }
    if (barcodes.length < 10) {
      const confirmSmall = window.confirm(
        `Only ${barcodes.length} items scanned. Are you sure you want to complete the despatch?`
      );
      if (!confirmSmall) {
        focusBarcodeInput();
        return;
      }
    }

    setLoading(true);
    setLoadingStep("Starting...");
    setLoadingDetail("Reconciling local items, outbox, and Firestore.");

    try {
      // 1) Reconcile everything (includes WAL recovery)
      setLoadingStep("Reconciling data...");
      setLoadingDetail("Making sure nothing is missing (local + WAL + Firestore).");

      const recon = await reconcileAll(reportDocId);

      setLoadingStep("Syncing scans...");
      setLoadingDetail("Uploading any pending scans. This must finish before completion.");
      setSyncing(true);
      await flushOutbox(reportDocId, { limit: 9999, force: true });
      setSyncing(false);


      const stillPending = loadOutbox(reportDocId).length;
      if (stillPending > 0) {
        alert(
          `⚠️ ${stillPending} scan(s) are still pending upload.\n\n` +
            `Fix the connection and retry.\n` +
            `Completion is blocked to prevent mismatch.`
        );
        setLoading(false);
        focusBarcodeInput();
        return;
      }

      // 3) Ensure any Unknown product codes have had a chance (best effort)
      const current = loadLocalItems(reportDocId);
      setLoadingStep("Finalising product codes...");
      setLoadingDetail("Filling in any missing product codes where possible.");

      await Promise.allSettled(
        current.map((it) => hydrateOneItemIfNeeded(reportDocId, it))
      );
      const hydrated = loadLocalItems(reportDocId);
      setBarcodes(hydrated);

      const unknowns = hydrated.filter((b) => !b.productCode || /^Unknown/.test(b.productCode));
      if (unknowns.length > 0) {
        const proceed = window.confirm(
          `${unknowns.length} item(s) still have unknown product codes. Submit anyway?`
        );
        if (!proceed) {
          setLoading(false);
          focusBarcodeInput();
          return;
        }
      }

      // 4) Final Firestore presence check (paranoid)
      const detailedRef = doc(firestore, "despatchreportsdetailed", reportDocId);
      const detailedSnap = await getDoc(detailedRef);
      setLoadingStep("Final verification...");
      setLoadingDetail("Checking Firestore contains every scanned item.");

      const firestoreItems = detailedSnap.exists() ? detailedSnap.data().items || [] : [];

      const firestoreIds = new Set(firestoreItems.map((b) => b.id));
      const missingInFirestore = hydrated.filter((b) => !firestoreIds.has(b.id));

      if (missingInFirestore.length > 0) {
        missingInFirestore.forEach((it) => queueForUpload(reportDocId, { ...it, _sync: "pending" }));
        await flushOutbox(reportDocId, { limit: 9999, force: true });

        const still = loadOutbox(reportDocId).length;
        if (still > 0) {
          alert(`⚠️ Some items still won’t sync (${still}). Can’t complete yet.`);
          setLoading(false);
          focusBarcodeInput();
          return;
        }
      }

      // 5) Mark report complete
      const reportRef = doc(firestore, "despatchreports", reportDocId);
      setLoadingStep("Marking report complete...");
      setLoadingDetail("Updating status in Firestore.");

      const finalList = loadLocalItems(reportDocId).map((x) => ({ ...x, _sync: "synced" }));
      const tableData = finalList.map((item, index) => ({
        "#": index + 1,
        "Serial Number": item.serialNumber,
        "Product Code": item.productCode,
        Type: item.special,
        Barcode: item.barcode,
      }));

      await completeDespatchAndNotify({
        writeCompletion: () =>
          completeDespatchRecord({
            reportRef,
            user: currentUser,
            finalItems: finalList,
            updateDocFn: updateDoc,
            serverTimestampFn: serverTimestamp,
          }),
        sendCompletionEmail: () =>
          axios.post(
            "https://europe-west2-peters-help-desk.cloudfunctions.net/sendEmail",
            {
              to: "transportadmin@telford-group.com, hortonwood@telford-group.com, mcampbell@telford-group.com",
              cc: "aogden@telford-group.com, sfoskett@telford-group.com, iwebb@telford-group.com, qualityinspection@telford-group.com, qualityinspectionpm@telford-group.com",
              subject: `Despatch Report #${reportNumber}`,
              text:
                `Despatch #${reportNumber} has been completed.\n\n` +
                `Reconcile: mergedFromFirestore=${recon.mergedFromFirestore}, queuedForUpload=${recon.queuedForUpload}.`,
              tableData,
            }
          ),
      });

      setLoadingStep("Cleaning up...");
      setLoadingDetail("Removing local cached data for this report.");


      // 7) Cleanup local storage for this report
      localStorage.removeItem(LOCAL_ITEMS_KEY(reportDocId));
      localStorage.removeItem(OUTBOX_KEY(reportDocId));
      localStorage.removeItem(WAL_KEY(reportDocId));
      try {
        sessionStorage.removeItem(LOCAL_ITEMS_KEY(reportDocId));
        sessionStorage.removeItem(OUTBOX_KEY(reportDocId));
        sessionStorage.removeItem(WAL_KEY(reportDocId));
      } catch {}

      alert("✅ Despatch completed and email sent!");
      navigate("/DespatchDashboard");
    } catch (error) {
      console.error("❌ Error completing despatch:", error);

      const msg = error?.message || String(error);
      alert(
        `Error completing despatch.\n\n` +
        `Step: ${loadingStep || "unknown"}\n` +
        `Details: ${loadingDetail || "n/a"}\n` +
        `Error: ${msg}`
      );
    } finally {

      setLoading(false);
      setSyncing(false);
      focusBarcodeInput();
    }
  };

if (loading) {
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-200 px-4">
      <div className="bg-white border border-gray-300 shadow-lg rounded-lg p-6 w-full max-w-xl">
        <h1 className="text-xl font-semibold text-gray-800 mb-2">
          Completing Despatch #{reportNumber ?? "…"}
        </h1>

        <div className="text-sm text-gray-700 mb-4">
          <div className="font-medium">{loadingStep || "Working..."}</div>
          {loadingDetail ? <div className="text-gray-500 mt-1">{loadingDetail}</div> : null}
        </div>

        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-blue-500"></div>
          <div className="text-xs text-gray-500">
            Don’t close this page. If you lose internet, we’ll keep trying when you’re back online.
          </div>
        </div>

        {pendingCount > 0 ? (
          <div className="mt-4 text-xs text-yellow-700">
            Pending uploads: {pendingCount}. Completing is blocked until these sync.
          </div>
        ) : null}
      </div>
    </div>
  );
}


  const rowClass = (item) => {
    if (item._sync === "pending") return "bg-yellow-50";
    if (item._sync === "failed") return "bg-red-50";
    return "";
  };

  return (
    <div className={`min-h-screen w-full flex flex-col items-center justify-center transition-colors duration-500 ${bgColorClass}`}>
      <div className="bg-white shadow-lg rounded-lg p-6 w-full max-w-2xl border border-gray-300 mx-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h1 className="text-xl font-semibold text-gray-800">
            Warehouse Despatch (Report #{reportNumber ?? "loading..."})
          </h1>

          <div className="flex items-center gap-2">
            <div
              className={`text-xs px-2 py-1 rounded border ${
                pendingCount > 0 ? "border-yellow-400 text-yellow-700" : "border-green-400 text-green-700"
              }`}
              title={lastSyncMsg || ""}
            >
              {pendingCount > 0 ? `${pendingCount} pending` : "All synced"}
              {syncing ? " • syncing..." : ""}
            </div>

            <button
              onClick={handleRetryPending}
              disabled={!reportDocId || pendingCount === 0 || syncing}
              className={`text-xs px-2 py-1 rounded border ${
                pendingCount === 0 || syncing
                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50 text-gray-700 border-gray-300"
              }`}
            >
              Retry pending
            </button>
          </div>
        </div>

        {lastSyncMsg ? <div className="text-xs text-gray-500 mb-3">{lastSyncMsg}</div> : null}

        {operationError ? (
          <div
            role="alert"
            className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            {operationError}
          </div>
        ) : null}

        <label htmlFor="barcode" className="block text-gray-700 font-medium mb-1">
          Scan or Enter Barcode:
        </label>
        <input
          type="text"
          id="barcode"
          value={currentBarcode}
          onChange={handleBarcodeChange}
          onKeyDown={handleBarcodeKeyDown}
          ref={barcodeInputRef}
          placeholder="Scan 13-digit barcode and press Enter"
          className="mb-4 w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />

        {barcodes.length > 0 && (
          <div className="mb-4 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-2 border-b">#</th>
                  <th className="px-2 py-2 border-b">Serial</th>
                  <th className="px-2 py-2 border-b">Product</th>
                  <th className="px-2 py-2 border-b">Type</th>
                  <th className="px-2 py-2 border-b">Full Barcode</th>
                  <th className="px-2 py-2 border-b">Sync</th>
                  <th className="px-2 py-2 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {barcodes.map((item, index) => (
                  <tr key={item.id} className={`text-center ${rowClass(item)}`}>
                    <td className="px-2 py-1 border-b">{barcodes.length - index}</td>
                    <td className="px-2 py-1 border-b">{item.serialNumber}</td>
                    <td className="px-2 py-1 border-b">{item.productCode}</td>
                    <td className="px-2 py-1 border-b">{item.special}</td>
                    <td className="px-2 py-1 border-b">{item.barcode}</td>
                    <td className="px-2 py-1 border-b">
                      {item._sync === "pending" ? (
                        <span className="text-yellow-700 text-xs font-semibold">Pending</span>
                      ) : (
                        <span className="text-green-700 text-xs font-semibold">Synced</span>
                      )}
                    </td>
                    <td className="px-2 py-1 border-b">
                      <div className="flex items-center justify-center gap-2">
                        {item._sync === "pending" && (
                          <button
                            onClick={() => handleRetryOne(item)}
                            className="bg-yellow-500 text-white text-xs px-2 py-1 rounded hover:bg-yellow-600"
                          >
                            Retry
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteItem(item)}
                          className="bg-red-500 text-white text-xs px-2 py-1 rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pendingCount > 0 && (
              <div className="mt-2 text-xs text-yellow-700">
                Some rows are pending upload. Scanning still works; completion is blocked until they sync.
              </div>
            )}
          </div>
        )}

        <button onClick={handleCompleteDespatch} className="bg-primary hover:bg-hover text-white px-4 py-2 rounded">
          Complete Despatch
        </button>
      </div>
    </div>
  );
};

export default DespatchReport;
